import { EnrichmentSynchronizationState } from "./types";

export const SFDC_AUTH_CALLBACK_RESPONSE = "hgForce.sfdcAuthCallbackResponse";

export const SFDC_AUTH_URLS = {
  production: "https://login.salesforce.com/services/oauth2/authorize",
  sandbox: "https://test.salesforce.com/services/oauth2/authorize",
} as const;

export const ACCOUNT_MATCHING_PREFERRED_COUNTRY_FIELD = {
  BILLING_COUNTRY: "BillingCountry",
} as const;

export const UNAVAILABLE_SYNC_STATES: EnrichmentSynchronizationState[] = [
  EnrichmentSynchronizationState.SYNC_IN_PROGRESS,
  EnrichmentSynchronizationState.INTEGRATION_UNHEALTHY,
  EnrichmentSynchronizationState.RECORDS_CAP_EXCEEDED,
  EnrichmentSynchronizationState.CREDITS_EXHAUSTED,
  EnrichmentSynchronizationState.CHOOSE_SPEND_OR_PRODUCT,
];

export const DEFAULT_ORG_NAMES = {
  production: "My SFDC Org",
  sandbox: "My Sandbox SFDC Org",
} as const;

export const POPUP_CONFIG = {
  width: 400,
  height: 700,
} as const;

export const DETAILS_PAGE_TEXTS = {
  errors: {
    invalidIntegrationId: "Invalid integration ID",
    integrationNotFound: "Integration not found",
    errorWithMessage: (message: string) => `Error: ${message}`,
    // generateReportError: (message: string) => `Failed to generate report: ${message}`, // Future use
  },
  emptyState: {
    title: "Integration not found",
    description:
      "The integration you're looking for doesn't exist or has been removed.",
    buttonText: "View all integrations",
  },
  authorizationError: {
    title: "Not Authorized",
    description:
      "You cannot access this integration. This may be due to insufficient permissions or restricted access.",
  },
  integrationError: {
    title: "Error Loading Integration",
  },
  banners: {
    firstIntegrationWelcome:
      "Welcome to your new integration! Once matching has completed, please select your products and click Sync Salesforce to kick off your first data sync.",
    resyncWelcome:
      "Welcome back! It looks like you have made changes to your integration settings. Please select your products and click Sync Salesforce to kick off your next data sync.",
  },
  productSelection: {
    label: "Product Selection",
    description:
      "Records will be enriched with technology install data for the products selected.",
    saveError:
      "Unable to save product selection. Please try again later. If the issue persists, please contact support.",
    quotaExceededError: "Products quota exceeded",
  },
  spendCategories: {
    label: "Spend Categories",
    description:
      "Records will be enriched with spend data for the categories selected.",
    saveError:
      "Unable to save spend categories selection. Please try again later. If the issue persists, please contact support.",
  },
  syncErrorsReport: {
    success:
      "Your Error Report is on its way! We're processing your request and you'll receive an email shortly with a link to download your CSV file.",
    error: (message: string) =>
      `Failed to generate sync errors report: ${message?.length ? message : "Unknown error, please try again later. If the issue persists, please contact support."}`,
  },
  sync: {
    completed: "Sync completed!",
    noProductOrSpendError:
      "Unable to sync: Please select at least one product or spend category in your integration settings.",
  },
  generic: {
    saving: "Saving...",
    closeButton: "×",
  },
  // success: {
  //   syncReportGenerated: "Sync errors report generated successfully!", // Future use
  // },
} as const;

export const INTEGRATIONS_DROPDOWN_TEXTS = {
  tooltips: {
    switchIntegration: "Switch or add Salesforce integrations",
    productionLimit: "Only one Production integration is permitted",
  },
  labels: {
    addProduction: "Add Production Integration",
    addSandbox: "Add Sandbox Integration",
    noIntegrationsConnected: "No integrations connected",
  },
} as const;

export const SYNC_SALESFORCE_BUTTON_TEXTS = {
  labels: {
    default: "Sync Salesforce",
    inProgress: "Sync In Progress",
  },
  tooltips: {
    [EnrichmentSynchronizationState.SYNC_IN_PROGRESS]: {
      title: "Sync In Progress",
      description:
        "Data is currently being synced to your Salesforce org. This may take a few minutes to complete.",
    },
    [EnrichmentSynchronizationState.INTEGRATION_UNHEALTHY]: {
      title: "Unable to Sync",
      description: "Reauthentication required.",
    },
    [EnrichmentSynchronizationState.RECORDS_CAP_EXCEEDED]: {
      title: "Unable to Sync",
      description:
        "Based on your configuration of Accounts and/or Leads, the total number of records exceeds our 3 million record threshold. We recommend enriching only Accounts to continue with your integration configuration. Please reach out to your CSM if you have any questions.",
    },
    [EnrichmentSynchronizationState.CREDITS_EXHAUSTED]: {
      title: "Unable to Sync",
      description:
        "Your organization has exhausted its data enrichment credits. Please reach out to your CSM to purchase additional credits.",
    },
    [EnrichmentSynchronizationState.CHOOSE_SPEND_OR_PRODUCT]: {
      title: "Configuration Required",
      description:
        "Please select at least one product or spend category in your integration settings to sync data.",
    },
    invalidSettings: {
      title: "Invalid Settings",
      description:
        "No data enrichment has been selected. Please confirm that your integration settings are correct, and at least one data type has been selected or fully configured.",
    },
  },
} as const;

export const EDITABLE_INTEGRATION_TITLE_TEXTS = {
  messages: {
    renameSuccess: "Organization renamed successfully",
    renameError: "Failed to rename organization",
  },
  validation: {
    minNonWhitespaceChars: 3,
    tooShort: (minChars: number) =>
      `Name must contain at least ${minChars} non-whitespace characters`,
  },
} as const;

export const SYNC_RESULTS_TEXTS = {
  title: "Sync Results",
  buttonText: "Get Report",
  labels: {
    total: "Total",
    matches: "Matches",
    notFound: "Not Found",
    lastUpdated: "Last Updated",
  },
  hints: {
    total:
      "The total number of Accounts and/or Lead records that HG sees in your Salesforce.",
    matched:
      "Accounts and/or Lead records from your Salesforce that were matched with companies from HG.",
    notMatched:
      "Accounts and/or Lead records from your Salesforce that we were not able to match with companies from HG.",
  },
  dateStates: {
    never: "Never",
    syncing: "Syncing...",
    invalid: "Invalid date",
  },
} as const;
